import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { softwareApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';

const boolLabel = (value) => (value ? 'Yes' : 'No');

const DetailField = ({ label, value }) => {
  const display =
    value === null || value === undefined || value === ''
      ? '—'
      : Array.isArray(value)
        ? value.length
          ? value.map((v) => (typeof v === 'string' ? formatEnumLabel(v) : v)).join(', ')
          : '—'
        : typeof value === 'boolean'
          ? boolLabel(value)
          : typeof value === 'string' && value.includes('_')
            ? formatEnumLabel(value)
            : String(value);

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
        {display}
      </Typography>
    </Grid>
  );
};

const Section = ({ title, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
        borderRadius: 2,
        bgcolor: isDark ? theme.palette.background.paper : '#fff',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Paper>
  );
};

const ArtificialIntelligenceDetailsViewPage = () => {
  const [searchParams] = useSearchParams();
  const softwareId = searchParams.get('softwareId');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!softwareId) {
        setError('No software selected.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await softwareApi.get(softwareId);
        const data = unwrapData(response);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Failed to load artificial intelligence details.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [softwareId]);

  const aiSummary = useMemo(() => {
    if (!detail) return null;

    const di = detail.dataInformation;
    const risk = detail.riskAssessment;
    const sources = detail.dataSources ?? [];
    const usesAi =
      risk?.usesArtificialIntelligence != null
        ? Boolean(risk.usesArtificialIntelligence)
        : Boolean(
            sources.length
              || di?.personalData
              || (di?.sensitiveDataCategories?.length ?? 0) > 0
              || di?.modelType
              || di?.trainingDataOrigin
          );

    return {
      softwareName: detail.softwareName || '—',
      internalReference: detail.internalReference || '—',
      usesArtificialIntelligence: usesAi,
      dataSourcesUsed: sources.map((s) => s.sourceName).filter(Boolean).join('\n') || '—',
      personalData: Boolean(di?.personalData),
      sensitiveDataCategories: di?.sensitiveDataCategories ?? [],
      modelType: di?.modelType || '',
      trainingDataOrigin: di?.trainingDataOrigin || '',
      knownBias: Boolean(risk?.knownBias),
      biasDescription: risk?.biasDescription || '',
      regulationTypes: detail.regulationTypes ?? [],
    };
  }, [detail]);

  return (
    <Box m={{ xs: '12px', sm: '20px' }}>
      <Header title="System Artificial Intelligence Details" />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : aiSummary ? (
        <>
          <Section title="Artificial intelligence overview">
            <DetailField
              label="Uses artificial intelligence"
              value={aiSummary.usesArtificialIntelligence}
            />
            <DetailField label="Known bias" value={aiSummary.knownBias} />
            <DetailField label="Bias description" value={aiSummary.biasDescription} />
            <DetailField label="Applicable regulations" value={aiSummary.regulationTypes} />
          </Section>

          <Section title="Data & model information">
            <DetailField label="Data sources used" value={aiSummary.dataSourcesUsed} />
            <DetailField label="Personal data involved" value={aiSummary.personalData} />
            <DetailField
              label="Sensitive data categories"
              value={aiSummary.sensitiveDataCategories}
            />
            <DetailField label="Model type" value={aiSummary.modelType} />
            <DetailField label="Training data origin" value={aiSummary.trainingDataOrigin} />
          </Section>

          <Divider sx={{ my: 1 }} />
        </>
      ) : null}
    </Box>
  );
};

export default ArtificialIntelligenceDetailsViewPage;
